import React, { useEffect, useCallback, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useChatPlatform } from './context/ChatPlatformContext';
import { useResponsive } from './hooks/useResponsive';
import Sidebar from './components/Sidebar';
import MobileHeader from './components/MobileHeader';
import UnauthHeader from './components/UnauthHeader';
import { TooltipProvider } from './components/Tooltip';
import NotificationPanel from '../components/NotificationPanel';
import LoginModal from '../components/LoginModal';
import UserMenuPopover from '../components/UserMenuPopover';
import UserSettingsModal from '../components/UserSettingsModal';
import { authApi, isAuthenticated, userApi } from '../utils/api';
import { clearStorageForLogout } from '../utils/storageUtils';
import { useSessionsActions, useIsSessionsInitializing } from './store/hooks';
import { useAgentSelection } from './hooks/useAgentSelection';

const NOTIFICATION_TYPE_INSIGHT = 'instrument_insight';
const INSIGHT_DETAIL_PATH = (documentId) => `/insights/${encodeURIComponent(documentId)}`;

const resolveInsightNotificationPath = (notification) => {
    if (notification?.type !== NOTIFICATION_TYPE_INSIGHT) return null;
    const documentId = notification?.meta?.documentId;
    if (typeof documentId !== 'string') return null;
    const trimmed = documentId.trim();
    return trimmed ? INSIGHT_DETAIL_PATH(trimmed) : null;
};

const HEADER_HEIGHTS = {
    MOBILE_AUTH: 56,
    MOBILE_UNAUTH: 56,
    DESKTOP_AUTH: 48,
    MOBILE_BUFFER: 12,
};

const getHeaderHeight = (isMobile, authenticated) => {
    if (isMobile && authenticated) return HEADER_HEIGHTS.MOBILE_AUTH;
    if (!authenticated) return HEADER_HEIGHTS.MOBILE_UNAUTH;
    if (!isMobile) return HEADER_HEIGHTS.DESKTOP_AUTH;
    return 0;
};

function ChatLayoutInner() {
    const { isMobile } = useResponsive();
    const {
        isSidebarOpen,
        showNotificationPanel,
        setShowNotificationPanel,
        notificationCount,
        isLoginOpen,
        setIsLoginOpen,
        chatCallbacksRef,
        isShareView,
        setIsShareView,
        isAdminView,
        setIsAdminView,
        isDraftChat,
        setIsDraftChat,
        sharedInfo,
        isLoading,
        isInputDisabled,
        editingSessionId,
        editingName,
        setEditingName,
        openMenuSessionId,
        setOpenMenuSessionId,
        isUserMenuOpen,
        setIsUserMenuOpen,
        currentUser,
        membershipLabel,
        activeSessionId,
        setActiveSessionId,
    } = useChatPlatform();

    const { fetch: fetchSessions } = useSessionsActions();
    const isInitializing = useIsSessionsInitializing();
    const navigate = useNavigate();
    const [isUserSettingsOpen, setIsUserSettingsOpen] = useState(false);
    const { agentId } = useAgentSelection();

    const buildPath = useCallback((path) => {
        if (!agentId) return path;
        const sep = path.includes('?') ? '&' : '?';
        return `${path}${sep}agent_id=${agentId}`;
    }, [agentId]);

    const handleNotificationClick = useCallback((notification) => {
        const insightPath = resolveInsightNotificationPath(notification);
        if (insightPath) {
            setShowNotificationPanel(false);
            navigate(insightPath);
            return;
        }
        if (chatCallbacksRef.current.handleNotificationClick) {
            chatCallbacksRef.current.handleNotificationClick(notification);
        } else {
            setShowNotificationPanel(false);
            navigate(buildPath(`/chat?source=notification&source_id=${notification.id}`), { replace: true });
        }
    }, [chatCallbacksRef, navigate, buildPath, setShowNotificationPanel, setIsShareView, setIsAdminView, setIsDraftChat, setActiveSessionId]);

    const showDraftLanding = !activeSessionId && !isShareView && !isAdminView && (isDraftChat || isInitializing);

    useEffect(() => {
        fetchSessions();
    }, [fetchSessions]);

    const headerHeight = getHeaderHeight(isMobile, isAuthenticated());

    return (
        <TooltipProvider>
        <div
            className="flex h-full bg-app-bg overflow-hidden"
            style={{ '--header-height': `${headerHeight}px` }}
        >
            <MobileHeader
                isShareView={isShareView}
                sharedInfo={sharedInfo}
                isAdminView={isAdminView}
                isDraftChat={isDraftChat}
                messages={[]}
                liveMessages={[]}
                isLoading={isLoading}
                isInputDisabled={isInputDisabled}
                handleNewChat={() => chatCallbacksRef.current.handleNewChat ? chatCallbacksRef.current.handleNewChat() : navigate(buildPath('/chat'))}
            />

            {!isAuthenticated() && !showDraftLanding && <UnauthHeader />}

            <Sidebar
                handleNewChat={() => chatCallbacksRef.current.handleNewChat ? chatCallbacksRef.current.handleNewChat() : navigate(buildPath('/chat'))}
                isLoading={isLoading}
                isInputDisabled={isInputDisabled}
                showNotificationPanel={showNotificationPanel}
                setShowNotificationPanel={setShowNotificationPanel}
                notificationCount={notificationCount}
                setNotificationCount={() => { }}
                isInitializing={isInitializing}
                editingSessionId={editingSessionId}
                editingName={editingName}
                setEditingName={setEditingName}
                openMenuSessionId={openMenuSessionId}
                setOpenMenuSessionId={setOpenMenuSessionId}
                switchSession={(id) => {
                    if (chatCallbacksRef.current.switchSession) {
                        chatCallbacksRef.current.switchSession(id);
                    } else {
                        navigate(buildPath(`/chat/${id}`));
                    }
                }}
                startEditing={(s) => chatCallbacksRef.current.startEditing?.(s)}
                handleRenameSession={(id, name) => chatCallbacksRef.current.handleRenameSession?.(id, name)}
                handleDeleteSession={(id) => chatCallbacksRef.current.handleDeleteSession?.(id)}
                setIsUserMenuOpen={setIsUserMenuOpen}
                membershipLabel={membershipLabel}
            />

            <UserMenuPopover
                isOpen={isUserMenuOpen}
                email={currentUser?.email || ''}
                onSettings={() => {
                    setIsUserMenuOpen(false);
                    if (chatCallbacksRef.current.openUserSettings) {
                        chatCallbacksRef.current.openUserSettings();
                    } else {
                        setIsUserSettingsOpen(true);
                    }
                }}
                onLogout={() => {
                    if (chatCallbacksRef.current.handleLogout) {
                        chatCallbacksRef.current.handleLogout();
                        return;
                    }
                    try {
                        authApi.logout();
                        userApi.clearCached();
                        clearStorageForLogout();
                    } catch { /* swallow */ }
                    window.location.replace('/login');
                }}
                onClose={() => setIsUserMenuOpen(false)}
            />

            <UserSettingsModal
                isOpen={isUserSettingsOpen}
                onClose={() => setIsUserSettingsOpen(false)}
                user={currentUser}
            />

            {showNotificationPanel && (
                <>
                    <div
                        className="hidden sm:block fixed inset-0 z-[99]"
                        onClick={() => setShowNotificationPanel(false)}
                    />
                    <div
                        className={`fixed top-0 bottom-0 z-[100] w-full sm:w-80 left-0 ${isSidebarOpen ? 'sm:left-64' : 'sm:left-[52px]'
                            }`}
                    >
                        <NotificationPanel
                            count={notificationCount}
                            onClose={() => setShowNotificationPanel(false)}
                            onNotificationClick={handleNotificationClick}
                        />
                    </div>
                </>
            )}

            <div 
                className="relative flex-1 flex flex-col bg-app-bg min-w-0"
            >
                <LoginModal
                    isOpen={isLoginOpen}
                    onClose={() => setIsLoginOpen(false)}
                    onSuccess={() => chatCallbacksRef.current.handleLoginSuccess?.()}
                />
                <Outlet />
            </div>

        </div>
        </TooltipProvider>
    );
}

export default function ChatLayout() {
    return (
        <ChatLayoutInner />
    );
}
